import React, { useState } from 'react';
import { Play, Trash2, GripVertical, Eye } from 'lucide-react';
import Button from './Button';
import VideoPopupModal from './VideoPopupModal';
import { RecordedVideo } from '../hooks/useScreenCapture';

interface VideoPreviewProps {
  video: RecordedVideo;
  onDelete?: (videoId: string) => void;
  isDragging?: boolean;
  dragHandleProps?: any;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  video,
  onDelete,
  isDragging = false,
  dragHandleProps
}) => {
  const [showVideoModal, setShowVideoModal] = useState(false);

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '00:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden transition-all duration-200 ${
        isDragging ? 'opacity-50 scale-95 shadow-lg' : 'hover:shadow-md'
      }`}>
        {/* Video Thumbnail */}
        <div className="relative bg-gray-100 h-48 flex items-center justify-center">
          {/* Video Icon */}
          <div className="flex flex-col items-center space-y-2">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Play className="h-8 w-8 text-blue-600" />
            </div>
            <span className="text-sm text-gray-600">Click to preview</span>
          </div>

          {/* Drag Handle */}
          <div
            {...dragHandleProps}
            className="absolute top-2 left-2 p-1 bg-black bg-opacity-50 rounded text-white cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </div>

          {/* Action Buttons */}
          <div className="absolute top-2 right-2 flex space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVideoModal(true)}
              className="p-1 bg-blue-600 bg-opacity-80 text-white hover:bg-opacity-100"
            >
              <Eye className="h-4 w-4" />
            </Button>
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(video.id)}
                className="p-1 bg-red-600 bg-opacity-80 text-white hover:bg-opacity-100"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Video Info */}
        <div className="p-3 space-y-2">
          <div className="text-sm font-medium text-gray-900">
            Recording {video.id.slice(-4)}
          </div>
          
          <div className="text-xs text-gray-500 space-y-1">
            <div>Duration: {formatTime(video.duration)}</div>
            <div>Size: {(video.blob.size / (1024 * 1024)).toFixed(1)} MB</div>
            <div>Type: {video.blob.type}</div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVideoModal(true)}
            leftIcon={<Eye className="h-3 w-3" />}
            className="w-full"
          >
            Preview Video
          </Button>
        </div>
      </div>

      {/* Video Popup Modal */}
      <VideoPopupModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        video={video}
      />
    </>
  );
};

export default VideoPreview;
